//
// places.ts — find anywhere by name
//
// Translation of `Networking/PlacesService.swift`.
//
// Open-Meteo's geocoding API is a separate, free, no-key service from its
// forecast API: it turns "Reykjavik" into a coordinate, a country, a
// population and a timezone. On a polar azimuthal map carrying thirteen
// ocean and continent labels and nothing else, this is what lets you find
// anywhere specific — and because it returns a coordinate, each result
// drops straight onto the projection as a `TrackedObject` with the same
// tap-to-inspect behaviour as everything else.
//

import type { Place, TrackedObject } from "../types";
import { qualifiedPlaceName } from "../types";
import { OPEN_METEO_GEOCODING, fetchJSON } from "./config";

interface RawResponse {
  results?: Array<{
    id: number;
    name: string;
    latitude: number;
    longitude: number;
    elevation?: number;
    country?: string;
    admin1?: string;
    population?: number;
    timezone?: string;
    feature_code?: string;
  }>;
}

export async function searchPlaces(query: string, count = 8): Promise<Place[]> {
  const trimmed = query.trim();
  if (trimmed.length < 2) return [];

  const url =
    `${OPEN_METEO_GEOCODING}?name=${encodeURIComponent(trimmed)}` +
    `&count=${count}&language=en&format=json`;
  const response = await fetchJSON<RawResponse>(url);

  return (response?.results ?? []).map((r) => ({
    id: r.id,
    name: r.name,
    country: r.country,
    admin1: r.admin1,
    latitude: r.latitude,
    longitude: r.longitude,
    elevationM: r.elevation,
    population: r.population,
    timezone: r.timezone,
    featureCode: r.feature_code,
  }));
}

/** A searched place as a map object, so it inspects like everything else. */
export function placeToTrackedObject(place: Place): TrackedObject {
  let typeLabel = "GEOGRAPHIC PLACE";
  if (place.population && place.population > 0) {
    typeLabel = `POPULATED PLACE — ${place.population.toLocaleString("en-US")} PEOPLE`;
  }
  if (place.featureCode === "PPLC") typeLabel = "NATIONAL CAPITAL";
  if (place.featureCode === "AIRP") typeLabel = "AIRPORT";

  return {
    id: `place-${place.id}`,
    name: qualifiedPlaceName(place),
    category: "place",
    typeLabel,
    latitude: place.latitude,
    longitude: place.longitude,
    dataSource: "Open-Meteo Geocoding",
    lastUpdated: place.timezone ?? "Fixed position",
  };
}
